import React, { useState, useEffect } from 'react';
import Toast from './Toast';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'danger' | 'warning' | 'info' | 'primary';
}

// A global event system to trigger toasts from anywhere in the app
type ToastEvent = CustomEvent<{
  message: string;
  type: 'success' | 'danger' | 'warning' | 'info' | 'primary';
}>;

declare global {
  interface WindowEventMap {
    'show-toast': ToastEvent;
  }
}

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const handleToastEvent = (event: ToastEvent) => {
      const { message, type } = event.detail;
      
      // Create a new toast item
      const newToast: ToastItem = {
        id: `toast-${new Date().getTime()}`,
        message,
        type
      };
      
      // Add the new toast to our list
      setToasts(prevToasts => [...prevToasts, newToast]);
    };

    // Add event listener
    window.addEventListener('show-toast', handleToastEvent as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('show-toast', handleToastEvent as EventListener);
    };
  }, []);

  // Handler to remove a toast
  const removeToast = (id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };

  return (
    <div className="toast-container position-fixed bottom-0 end-0 p-3">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

// Utility function to show a toast from anywhere in the app
export const showToast = (
  message: string, 
  type: 'success' | 'danger' | 'warning' | 'info' | 'primary' = 'primary'
) => {
  const toastEvent = new CustomEvent('show-toast', {
    detail: { message, type }
  });
  
  window.dispatchEvent(toastEvent);
};

export default ToastContainer;
